import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { settingsApi } from "../services/settingsApi";
import { notificationSettingsApi } from "../services/notificationSettingsApi";
import FeedbackModal from "../components/ui/FeedbackModal";
import { useTheme } from "../context/ThemeContext";
import Can from "../components/access/Can";
import RoleManagement from "../components/settings/RoleManagement";
import UserManagement from "../components/settings/UserManagement";
import {
  FiUser,
  FiEye,
  FiEyeOff,
  FiSun,
  FiMoon,
  FiCreditCard,
  FiDollarSign,
  FiPercent,
  FiType,
  FiSave,
  FiShield,
  FiLock,
  FiMail,
  FiImage,
  FiBriefcase,
  FiCheckCircle,
  FiInfo,
  FiRefreshCw,
  FiMessageCircle,
  FiInbox,
} from "react-icons/fi";

const currencyOptions = ["GBP", "USD", "EUR", "PKR", "AED", "SAR"];

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const DEFAULT_WHATSAPP_TEMPLATE_BODY = `Container delivery booking confirmed.

Serial No: {{1}}
Container No: {{2}}
Booking/BL No: {{3}}
Delivery Booked On: {{4}}
Expected Arrival: {{5}}`;

const WHATSAPP_TEMPLATE_VARIABLES = [
  { token: "{{1}}", label: "Serial No", sample: "26-0001" },
  { token: "{{2}}", label: "Container No", sample: "MSCU1234567" },
  { token: "{{3}}", label: "Booking/BL No", sample: "BL-874521" },
  { token: "{{4}}", label: "Delivery Booked On", sample: "17/07/2026" },
  { token: "{{5}}", label: "Expected Arrival", sample: "24/07/2026" },
];

const tabs = [
  { key: "profile", label: "Profile" },
  { key: "password", label: "Password & Security" },
  { key: "company", label: "Company Info" },
  { key: "invoice", label: "Invoice Settings" },
  { key: "notifications", label: "Alerts & Documents" },
  { key: "roles", label: "Roles & Permissions" },
  { key: "users", label: "User Access" },
  { key: "appearance", label: "Appearance" },
];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-4">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
          <Icon className="text-lg" />
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-950 dark:text-slate-50">
            {title}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        </div>
      </div>

      {action}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">
        {label}
      </label>
      {children}
    </div>
  );
}

function TextInput({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950 ${className}`}
    />
  );
}

function SelectInput({ className = "", ...props }) {
  return (
    <select
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950 ${className}`}
    />
  );
}

function TextArea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950 ${className}`}
    />
  );
}

function Toggle({ enabled, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative h-6 w-11 rounded-full transition ${
        enabled ? "bg-blue-600" : "bg-slate-300 dark:bg-slate-700"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      aria-pressed={enabled}
      aria-disabled={disabled}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          enabled ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
}

function InfoRow({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 last:border-b-0 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-4">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-slate-50 text-blue-600 dark:bg-slate-950 dark:text-blue-300">
          <Icon />
        </div>

        <div>
          <h3 className="font-semibold text-slate-950 dark:text-slate-50">
            {title}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        </div>
      </div>

      {right}
    </div>
  );
}

function EmptyAccessCard({ title }) {
  return (
    <Card className="p-8 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300">
        <FiLock className="text-xl" />
      </div>

      <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-slate-50">
        No Access
      </h3>

      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
        You do not have permission to view {title}.
      </p>
    </Card>
  );
}

export default function Settings() {
  const { theme, setTheme } = useTheme();

  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmailAutomation, setSavingEmailAutomation] = useState(false);

  const [user, setUser] = useState(null);
  const [name, setName] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [settings, setSettings] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    companyAddress: "",
    logoDataUrl: "",
    invoiceShowLogo: true,
    invoiceShowTitle: true,
    invoiceTitle: "INVOICE",
    invoiceTemplate: "modern",
    invoiceCurrency: "GBP",
    invoiceTaxEnabled: false,
    invoiceTaxRate: 0,
    invoiceDiscountEnabled: false,
    invoiceDiscountRate: 0,
  });

  const [notificationSettings, setNotificationSettings] = useState({
    whatsappEnabled: false,
    whatsappRecipients: "",
    whatsappTemplateName: "container_delivery_booking_alert",
    whatsappTemplateLanguage: "en_GB",
    whatsappTemplateBody: DEFAULT_WHATSAPP_TEMPLATE_BODY,
    emailAutomationEnabled: false,
    emailAllowedSenders: "",
    emailPollMinutes: 5,
    whatsappConfigured: false,
    emailConfigured: false,
  });

  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const meRes = await api.get("/auth/me");
        const freshUser = meRes.data?.user || null;

        setUser(freshUser);
        setName(freshUser?.name || "");

        const settingsRes = await settingsApi.getMy();
        const s = settingsRes.data?.settings || {};

        setSettings((prev) => ({
          ...prev,
          ...s,
          invoiceCurrency: s.invoiceCurrency || "GBP",
          invoiceShowLogo:
            s.invoiceShowLogo === undefined ? true : Boolean(s.invoiceShowLogo),
          invoiceShowTitle:
            s.invoiceShowTitle === undefined
              ? true
              : Boolean(s.invoiceShowTitle),
          invoiceTaxEnabled: Boolean(s.invoiceTaxEnabled),
          invoiceDiscountEnabled: Boolean(s.invoiceDiscountEnabled),
          invoiceTaxRate: Number(s.invoiceTaxRate || 0),
          invoiceDiscountRate: Number(s.invoiceDiscountRate || 0),
        }));

        try {
          const notificationRes = await notificationSettingsApi.get();
          const notificationData = notificationRes.data?.settings || {};

          setNotificationSettings((prev) => ({
            ...prev,
            ...notificationData,
            whatsappEnabled: toBoolean(notificationData.whatsappEnabled),
            emailAutomationEnabled: toBoolean(
              notificationData.emailAutomationEnabled
            ),
            whatsappConfigured:
              notificationData.whatsappConfigured === undefined
                ? prev.whatsappConfigured
                : toBoolean(notificationData.whatsappConfigured),
            emailConfigured:
              notificationData.emailConfigured === undefined
                ? prev.emailConfigured
                : toBoolean(notificationData.emailConfigured),
            whatsappTemplateBody:
              notificationData.whatsappTemplateBody ||
              DEFAULT_WHATSAPP_TEMPLATE_BODY,
            emailAllowedSenders:
              notificationData.emailAllowedSenders || "",
            emailPollMinutes: Number(
              notificationData.emailPollMinutes || 5
            ),
          }));
        } catch (notificationError) {
          if (notificationError?.response?.status !== 403) {
            console.error("LOAD NOTIFICATION SETTINGS ERROR:", notificationError);
          }
        }

        if (freshUser) {
          localStorage.setItem("user", JSON.stringify(freshUser));
        }
      } catch {
        const savedUser = localStorage.getItem("user");

        if (savedUser) {
          try {
            const parsed = JSON.parse(savedUser);
            setUser(parsed);
            setName(parsed.name || "");
          } catch {
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!feedback.open || feedback.type !== "success") return;

    const timer = setTimeout(() => {
      setFeedback((prev) => ({ ...prev, open: false }));
    }, 2200);

    return () => clearTimeout(timer);
  }, [feedback.open, feedback.type]);

  const passwordStrength = useMemo(() => {
    const issues = [];
    const password = newPassword || "";

    if (password && password.length < 8) issues.push("At least 8 characters");
    if (password && !/[a-z]/.test(password)) issues.push("One lowercase letter");
    if (password && !/[A-Z]/.test(password)) issues.push("One uppercase letter");
    if (password && !/[0-9]/.test(password)) issues.push("One number");
    if (password && !/[^A-Za-z0-9]/.test(password)) {
      issues.push("One special character");
    }

    let label = "Weak";
    if (issues.length <= 1 && password.length >= 8) label = "Strong";
    else if (issues.length <= 3) label = "Medium";

    return { issues, label };
  }, [newPassword]);

  const whatsappTemplatePreview = useMemo(() => {
    let preview =
      notificationSettings.whatsappTemplateBody ||
      DEFAULT_WHATSAPP_TEMPLATE_BODY;

    WHATSAPP_TEMPLATE_VARIABLES.forEach(({ token, sample }) => {
      preview = preview.split(token).join(sample);
    });

    return preview;
  }, [notificationSettings.whatsappTemplateBody]);

  const showSaveBar = ["profile", "company", "invoice", "notifications"].includes(activeTab);

  const closeFeedback = () => {
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateNotificationSetting = (key, value) => {
    setNotificationSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleEmailAutomationToggle = async (enabled) => {
    if (savingEmailAutomation) return;

    if (
      enabled &&
      !String(notificationSettings.emailAllowedSenders || "").trim()
    ) {
      setFeedback({
        open: true,
        type: "error",
        title: "Allowed sender required",
        message:
          "Add at least one approved sender email before enabling email automation.",
      });
      return;
    }

    const previousValue = Boolean(
      notificationSettings.emailAutomationEnabled
    );

    setNotificationSettings((prev) => ({
      ...prev,
      emailAutomationEnabled: Boolean(enabled),
    }));

    setSavingEmailAutomation(true);

    try {
      const response = await notificationSettingsApi.update({
        whatsappEnabled: Boolean(
          notificationSettings.whatsappEnabled
        ),
        whatsappRecipients:
          notificationSettings.whatsappRecipients || "",
        whatsappTemplateName:
          notificationSettings.whatsappTemplateName ||
          "container_delivery_booking_alert",
        whatsappTemplateLanguage:
          notificationSettings.whatsappTemplateLanguage || "en_GB",
        whatsappTemplateBody:
          notificationSettings.whatsappTemplateBody ||
          DEFAULT_WHATSAPP_TEMPLATE_BODY,
        emailAutomationEnabled: Boolean(enabled),
        emailAllowedSenders:
          notificationSettings.emailAllowedSenders || "",
        emailPollMinutes: Math.max(
          1,
          Number(notificationSettings.emailPollMinutes || 5)
        ),
      });

      const saved = response.data?.settings || {};

      setNotificationSettings((prev) => ({
        ...prev,
        ...saved,
        whatsappEnabled:
          saved.whatsappEnabled === undefined
            ? prev.whatsappEnabled
            : toBoolean(saved.whatsappEnabled),
        emailAutomationEnabled:
          saved.emailAutomationEnabled === undefined
            ? Boolean(enabled)
            : toBoolean(saved.emailAutomationEnabled),
        whatsappConfigured:
          saved.whatsappConfigured === undefined
            ? prev.whatsappConfigured
            : toBoolean(saved.whatsappConfigured),
        emailConfigured:
          saved.emailConfigured === undefined
            ? prev.emailConfigured
            : toBoolean(saved.emailConfigured),
        emailAllowedSenders:
          saved.emailAllowedSenders ?? prev.emailAllowedSenders,
        emailPollMinutes: Number(
          saved.emailPollMinutes ?? prev.emailPollMinutes ?? 5
        ),
      }));

      setFeedback({
        open: true,
        type: "success",
        title: enabled
          ? "Email automation enabled"
          : "Email automation disabled",
        message: enabled
          ? "The inbox will now be checked for container documents."
          : "Automatic email document importing has been stopped.",
      });
    } catch (error) {
      setNotificationSettings((prev) => ({
        ...prev,
        emailAutomationEnabled: previousValue,
      }));

      setFeedback({
        open: true,
        type: "error",
        title: "Unable to save email automation",
        message:
          error.response?.data?.message ||
          error.message ||
          "Email automation could not be updated.",
      });
    } finally {
      setSavingEmailAutomation(false);
    }
  };

  const clearPasswordFields = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const onLogoChange = async (file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    updateSetting("logoDataUrl", dataUrl);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      if (activeTab === "notifications") {
        const allowedSenders = String(
          notificationSettings.emailAllowedSenders || ""
        ).trim();

        if (
          notificationSettings.emailAutomationEnabled &&
          !allowedSenders
        ) {
          setFeedback({
            open: true,
            type: "error",
            title: "Allowed sender required",
            message:
              "Add at least one approved sender email before enabling email automation.",
          });
          return;
        }

        const templateBody = String(
          notificationSettings.whatsappTemplateBody ||
            DEFAULT_WHATSAPP_TEMPLATE_BODY
        ).trim();

        const missingVariables = WHATSAPP_TEMPLATE_VARIABLES.filter(
          ({ token }) => !templateBody.includes(token)
        );

        if (missingVariables.length) {
          setFeedback({
            open: true,
            type: "error",
            title: "Template variables missing",
            message: `Please add ${missingVariables
              .map(({ token, label }) => `${token} (${label})`)
              .join(", ")} to the WhatsApp template.`,
          });
          return;
        }

        const response = await notificationSettingsApi.update({
          whatsappEnabled: Boolean(notificationSettings.whatsappEnabled),
          whatsappRecipients: notificationSettings.whatsappRecipients,
          whatsappTemplateName:
            notificationSettings.whatsappTemplateName ||
            "container_delivery_booking_alert",
          whatsappTemplateLanguage:
            notificationSettings.whatsappTemplateLanguage || "en_GB",
          whatsappTemplateBody: templateBody,
          emailAutomationEnabled: Boolean(
            notificationSettings.emailAutomationEnabled
          ),
          emailAllowedSenders: notificationSettings.emailAllowedSenders,
          emailPollMinutes: Number(notificationSettings.emailPollMinutes || 5),
        });

        const savedNotificationSettings =
          response.data?.settings || {};

        setNotificationSettings((prev) => ({
          ...prev,
          ...savedNotificationSettings,
          whatsappEnabled:
            savedNotificationSettings.whatsappEnabled === undefined
              ? prev.whatsappEnabled
              : toBoolean(savedNotificationSettings.whatsappEnabled),
          emailAutomationEnabled:
            savedNotificationSettings.emailAutomationEnabled === undefined
              ? prev.emailAutomationEnabled
              : toBoolean(
                  savedNotificationSettings.emailAutomationEnabled
                ),
          whatsappConfigured:
            savedNotificationSettings.whatsappConfigured === undefined
              ? prev.whatsappConfigured
              : toBoolean(
                  savedNotificationSettings.whatsappConfigured
                ),
          emailConfigured:
            savedNotificationSettings.emailConfigured === undefined
              ? prev.emailConfigured
              : toBoolean(savedNotificationSettings.emailConfigured),
          emailPollMinutes: Number(
            savedNotificationSettings.emailPollMinutes ??
              prev.emailPollMinutes ??
              5
          ),
        }));

        setFeedback({
          open: true,
          type: "success",
          title: "Automation saved",
          message: "WhatsApp and email automation settings were updated.",
        });
        return;
      }

      const profilePayload = { name: name.trim() };

      const appSettingsPayload = {
        ...settings,
        invoiceTitle: settings.invoiceTitle || "INVOICE",
        invoiceCurrency: settings.invoiceCurrency || "GBP",
        invoiceTaxRate: Number(settings.invoiceTaxRate || 0),
        invoiceDiscountRate: Number(settings.invoiceDiscountRate || 0),
      };

      const [profileRes, settingsRes] = await Promise.all([
        api.put("/auth/update-profile", profilePayload),
        settingsApi.updateMy(appSettingsPayload),
      ]);

      const updatedUser = profileRes.data?.user || user;

      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
        window.dispatchEvent(new Event("user-updated"));
      }

      setSettings((prev) => ({
        ...prev,
        ...(settingsRes.data?.settings || {}),
      }));

      setFeedback({
        open: true,
        type: "success",
        title: "Saved",
        message: "Settings updated successfully.",
      });
    } catch (err) {
      setFeedback({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setFeedback({
        open: true,
        type: "error",
        title: "Missing fields",
        message:
          "Please fill in your current password, new password, and confirmation.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setFeedback({
        open: true,
        type: "error",
        title: "Passwords don't match",
        message: "New password and confirm password do not match.",
      });
      return;
    }

    setSavingPassword(true);

    try {
      await api.put("/auth/update-profile", {
        oldPassword,
        newPassword,
        confirmPassword,
      });

      clearPasswordFields();

      setFeedback({
        open: true,
        type: "success",
        title: "Password updated",
        message: "Your password has been changed successfully.",
      });
    } catch (err) {
      clearPasswordFields();

      setFeedback({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const renderProfileTab = () => (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
      <div className="space-y-6">
        <Card>
          <SectionHeader
            icon={FiUser}
            title="Personal Information"
            subtitle="Update your account details and identity."
          />

          <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
            <Field label="Full Name">
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </Field>

            <Field label="Email Address">
              <TextInput value={user?.email || ""} disabled />
            </Field>

            <Field label="Role">
              <TextInput value={user?.roleName || user?.role || ""} disabled />
            </Field>

            <Field label="Account Status">
              <TextInput
                value={user?.isActive === false ? "Inactive" : "Active"}
                disabled
              />
            </Field>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <InfoRow
            icon={FiMail}
            title="Email Address"
            subtitle="This email is used for login and account access."
            right={
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {user?.email || "-"}
              </span>
            }
          />

          <InfoRow
            icon={FiShield}
            title="Access Role"
            subtitle="Your role controls page access and system permissions."
            right={
              <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                {user?.roleName || user?.role || "User"}
              </span>
            }
          />
        </Card>
      </div>

      <Card>
        <SectionHeader
          icon={FiImage}
          title="Profile Preview"
          subtitle="How your profile appears in the dashboard."
        />

        <div className="flex flex-col items-center px-6 py-10 text-center">
          <div className="grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-3xl font-bold text-white shadow-lg shadow-blue-200 dark:shadow-none">
            {(name || user?.name || "A").slice(0, 1).toUpperCase()}
          </div>

          <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-slate-50">
            {name || user?.name || "Admin"}
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {user?.email || "admin@example.com"}
          </p>

          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {user?.isActive === false ? "Inactive" : "Active"}
          </div>
        </div>
      </Card>
    </div>
  );

  const renderPasswordTab = () => (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
      <Card>
        <SectionHeader
          icon={FiShield}
          title="Password & Security"
          subtitle="Change your password and secure your account."
        />

        <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Current Password">
              <div className="relative">
                <TextInput
                  type={showOldPassword ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
                >
                  {showOldPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </Field>
          </div>

          <Field label="New Password">
            <div className="relative">
              <TextInput
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
              >
                {showNewPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {newPassword ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Strength:{" "}
                <span className="font-semibold">{passwordStrength.label}</span>
                {passwordStrength.issues.length > 0
                  ? ` — ${passwordStrength.issues.join(", ")}`
                  : ""}
              </p>
            ) : null}
          </Field>

          <Field label="Confirm New Password">
            <div className="relative">
              <TextInput
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </Field>

          <div className="md:col-span-2 flex justify-end">
            <button
              type="button"
              onClick={handlePasswordSave}
              disabled={
                savingPassword ||
                (!oldPassword && !newPassword && !confirmPassword)
              }
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              <FiLock />
              {savingPassword ? "Updating password..." : "Update Password"}
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeader
          icon={FiInfo}
          title="Security Tips"
          subtitle="Use a strong password for better protection."
        />

        <div className="space-y-4 p-6">
          {[
            "Use at least 8 characters.",
            "Add uppercase and lowercase letters.",
            "Use numbers and special characters.",
            "Do not reuse an old password.",
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300"
            >
              <FiCheckCircle className="text-emerald-500" />
              {item}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderCompanyTab = () => (
    <Card>
      <SectionHeader
        icon={FiBriefcase}
        title="Company Information"
        subtitle="These details can appear on invoices and business documents."
      />

      <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
        <Field label="Company Name">
          <TextInput
            value={settings.companyName}
            onChange={(e) => updateSetting("companyName", e.target.value)}
            placeholder="Your company"
          />
        </Field>

        <Field label="Company Email">
          <TextInput
            value={settings.companyEmail}
            onChange={(e) => updateSetting("companyEmail", e.target.value)}
            placeholder="company@example.com"
          />
        </Field>

        <Field label="Company Phone">
          <TextInput
            value={settings.companyPhone}
            onChange={(e) => updateSetting("companyPhone", e.target.value)}
            placeholder="+44..."
          />
        </Field>

        <Field label="Invoice Currency">
          <SelectInput
            value={settings.invoiceCurrency}
            onChange={(e) => updateSetting("invoiceCurrency", e.target.value)}
          >
            {currencyOptions.map((cur) => (
              <option key={cur} value={cur}>
                {cur}
              </option>
            ))}
          </SelectInput>
        </Field>

        <div className="md:col-span-2">
          <Field label="Company Address">
            <TextArea
              value={settings.companyAddress}
              onChange={(e) => updateSetting("companyAddress", e.target.value)}
              rows="4"
              placeholder="Address..."
            />
          </Field>
        </div>
      </div>
    </Card>
  );

  const renderInvoiceTab = () => (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
      <Card>
        <SectionHeader
          icon={FiCreditCard}
          title="Invoice Settings"
          subtitle="Set invoice defaults, tax, discount, logo, and template."
        />

        <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <InfoRow
              icon={FiImage}
              title="Show Logo"
              subtitle="Display your company logo on invoices."
              right={
                <Toggle
                  enabled={settings.invoiceShowLogo}
                  onChange={(v) => updateSetting("invoiceShowLogo", v)}
                />
              }
            />
          </div>

          <div className="md:col-span-2">
            <Field label="Logo">
              <div className="flex flex-wrap items-center gap-4">
                <label className="cursor-pointer rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-blue-950">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await onLogoChange(file);
                    }}
                  />
                  Upload logo
                </label>

                {settings.logoDataUrl ? (
                  <img
                    src={settings.logoDataUrl}
                    alt="Logo preview"
                    className="h-14 w-14 rounded-xl border border-slate-200 object-cover dark:border-slate-700"
                  />
                ) : (
                  <span className="text-sm text-slate-400">
                    No logo uploaded
                  </span>
                )}
              </div>
            </Field>
          </div>

          <Field label="Invoice Title">
            <TextInput
              value={settings.invoiceTitle}
              onChange={(e) => updateSetting("invoiceTitle", e.target.value)}
              placeholder="INVOICE"
            />
          </Field>

          <Field label="Template">
            <SelectInput
              value={settings.invoiceTemplate}
              onChange={(e) => updateSetting("invoiceTemplate", e.target.value)}
            >
              <option value="modern">Modern</option>
              <option value="classic">Classic</option>
              <option value="minimal">Minimal</option>
            </SelectInput>
          </Field>

          <Field label="Currency">
            <SelectInput
              value={settings.invoiceCurrency}
              onChange={(e) => updateSetting("invoiceCurrency", e.target.value)}
            >
              {currencyOptions.map((cur) => (
                <option key={cur} value={cur}>
                  {cur}
                </option>
              ))}
            </SelectInput>
          </Field>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-950 dark:text-slate-50">
                  Show Invoice Title
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Display title on printed invoices.
                </p>
              </div>
              <Toggle
                enabled={settings.invoiceShowTitle}
                onChange={(v) => updateSetting("invoiceShowTitle", v)}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-950 dark:text-slate-50">
                  Tax
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Enable tax fields on invoice.
                </p>
              </div>
              <Toggle
                enabled={settings.invoiceTaxEnabled}
                onChange={(v) => updateSetting("invoiceTaxEnabled", v)}
              />
            </div>
          </div>

          <Field label="Tax Rate (%)">
            <TextInput
              type="number"
              min="0"
              step="0.01"
              value={settings.invoiceTaxRate}
              onChange={(e) => updateSetting("invoiceTaxRate", e.target.value)}
            />
          </Field>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-950 dark:text-slate-50">
                  Discount
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Enable discount fields on invoice.
                </p>
              </div>
              <Toggle
                enabled={settings.invoiceDiscountEnabled}
                onChange={(v) => updateSetting("invoiceDiscountEnabled", v)}
              />
            </div>
          </div>

          <Field label="Discount Rate (%)">
            <TextInput
              type="number"
              min="0"
              step="0.01"
              value={settings.invoiceDiscountRate}
              onChange={(e) =>
                updateSetting("invoiceDiscountRate", e.target.value)
              }
            />
          </Field>
        </div>
      </Card>

      <Card>
        <SectionHeader
          icon={FiCreditCard}
          title="Invoice Preview"
          subtitle="Quick view of invoice preferences."
        />

        <div className="p-6">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            {settings.invoiceShowLogo && settings.logoDataUrl ? (
              <img
                src={settings.logoDataUrl}
                alt="Logo"
                className="mb-4 h-16 w-16 rounded-2xl object-cover"
              />
            ) : null}

            {settings.invoiceShowTitle ? (
              <h3 className="text-2xl font-black tracking-wide text-slate-950 dark:text-slate-50">
                {settings.invoiceTitle || "INVOICE"}
              </h3>
            ) : null}

            <div className="mt-5 space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center gap-2">
                <FiDollarSign /> Currency: {settings.invoiceCurrency}
              </div>

              <div className="flex items-center gap-2">
                <FiPercent /> Tax:{" "}
                {settings.invoiceTaxEnabled
                  ? `${settings.invoiceTaxRate}%`
                  : "Off"}
              </div>

              <div className="flex items-center gap-2">
                <FiPercent /> Discount:{" "}
                {settings.invoiceDiscountEnabled
                  ? `${settings.invoiceDiscountRate}%`
                  : "Off"}
              </div>

              <div className="flex items-center gap-2">
                <FiType /> Template: {settings.invoiceTemplate}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );


  const renderNotificationsTab = () => (
    <Can
      moduleKey="settings"
      action="read"
      fallback={<EmptyAccessCard title="alerts and email documents" />}
    >
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <SectionHeader
            icon={FiMessageCircle}
            title="WhatsApp Booking Alerts"
            subtitle="Send a WhatsApp message when a container delivery is booked or changed."
            action={
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  notificationSettings.whatsappConfigured
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                }`}
              >
                {notificationSettings.whatsappConfigured
                  ? "Ready"
                  : "WhatsApp setup needed"}
              </span>
            }
          />

          <div className="space-y-6 p-6">
            <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div>
                <h3 className="font-semibold text-slate-950 dark:text-slate-50">
                  Send WhatsApp alerts
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Turn this on after the WhatsApp number has been connected.
                </p>
              </div>
              <Toggle
                enabled={notificationSettings.whatsappEnabled}
                onChange={(value) =>
                  updateNotificationSetting("whatsappEnabled", value)
                }
              />
            </div>

            <Field label="Who should receive the alerts?">
              <TextArea
                rows={4}
                value={notificationSettings.whatsappRecipients}
                onChange={(e) =>
                  updateNotificationSetting(
                    "whatsappRecipients",
                    e.target.value
                  )
                }
                placeholder={`447700900001\n447700900002`}
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Add one WhatsApp number per line with the country code. Example:
                447700900001.
              </p>
            </Field>

            <Field label="Message sent to the team">
              <TextArea
                rows={9}
                maxLength={2000}
                value={notificationSettings.whatsappTemplateBody}
                onChange={(e) =>
                  updateNotificationSetting(
                    "whatsappTemplateBody",
                    e.target.value
                  )
                }
                placeholder={DEFAULT_WHATSAPP_TEMPLATE_BODY}
                className="text-sm leading-6"
              />

              <div className="mt-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  These details are added automatically:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {WHATSAPP_TEMPLATE_VARIABLES.map(({ token, label }) => (
                    <span
                      key={token}
                      className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 shadow-sm dark:bg-slate-900 dark:text-blue-300"
                    >
                      {label} {token}
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-blue-700 dark:text-blue-300">
                  You can change the wording, but keep these five placeholders in
                  the message.
                </p>
              </div>
            </Field>

            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <h3 className="text-sm font-bold text-slate-950 dark:text-slate-50">
                    Message preview
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    This is how the alert will look with example container details.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    updateNotificationSetting(
                      "whatsappTemplateBody",
                      DEFAULT_WHATSAPP_TEMPLATE_BODY
                    )
                  }
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Use standard message
                </button>
              </div>

              <div className="bg-[#efeae2] p-5 dark:bg-slate-950">
                <div className="ml-auto max-w-[520px] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-[#d9fdd3] px-4 py-3 text-sm leading-6 text-slate-900 shadow-sm dark:bg-emerald-950 dark:text-emerald-100">
                  {whatsappTemplatePreview}
                </div>
              </div>
            </div>

            <details className="rounded-2xl border border-slate-200 dark:border-slate-800">
              <summary className="cursor-pointer px-4 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Advanced WhatsApp settings
              </summary>

              <div className="grid grid-cols-1 gap-5 border-t border-slate-200 p-4 dark:border-slate-800 md:grid-cols-2">
                <Field label="WhatsApp template name">
                  <TextInput
                    value={notificationSettings.whatsappTemplateName}
                    onChange={(e) =>
                      updateNotificationSetting(
                        "whatsappTemplateName",
                        e.target.value
                      )
                    }
                    placeholder="container_delivery_booking_alert"
                  />
                </Field>

                <Field label="Language">
                  <SelectInput
                    value={notificationSettings.whatsappTemplateLanguage}
                    onChange={(e) =>
                      updateNotificationSetting(
                        "whatsappTemplateLanguage",
                        e.target.value
                      )
                    }
                  >
                    <option value="en_GB">English (UK)</option>
                    <option value="en_US">English (US)</option>
                  </SelectInput>
                </Field>

                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400 md:col-span-2">
                  These values normally stay unchanged. They must match the
                  approved message in WhatsApp Manager.
                </p>
              </div>
            </details>
          </div>
        </Card>

        <Card>
          <SectionHeader
            icon={FiInbox}
            title="Email Documents"
            subtitle="Automatically add emailed documents to the correct container."
            action={
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  notificationSettings.emailConfigured
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                }`}
              >
                {notificationSettings.emailConfigured
                  ? "Ready"
                  : "Email setup needed"}
              </span>
            }
          />

          <div className="space-y-6 p-6">
            <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div>
                <h3 className="font-semibold text-slate-950 dark:text-slate-50">
                  Import documents from email
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  The system checks new emails and matches attachments to a
                  container.
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Toggle
                  enabled={notificationSettings.emailAutomationEnabled}
                  onChange={handleEmailAutomationToggle}
                  disabled={savingEmailAutomation}
                />

                {savingEmailAutomation ? (
                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">
                    Saving...
                  </span>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950">
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                How it works
              </p>
              <p className="mt-1 text-sm leading-6 text-emerald-700 dark:text-emerald-300">
                The supplier should include the container number, booking/BL
                number, or serial number in the email subject or file name.
              </p>
            </div>

            <Field label="Only accept documents from">
              <TextArea
                rows={4}
                value={notificationSettings.emailAllowedSenders}
                onChange={(e) =>
                  updateNotificationSetting(
                    "emailAllowedSenders",
                    e.target.value
                  )
                }
                placeholder={`supplier@example.com\n@forwarder.com`}
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Add at least one approved email address or company domain per
                line. Exact email addresses are recommended for the safest
                document importing.
              </p>
            </Field>

            <Field label="Check the inbox every">
              <div className="flex items-center gap-3">
                <TextInput
                  type="number"
                  min="1"
                  max="60"
                  value={notificationSettings.emailPollMinutes}
                  onChange={(e) =>
                    updateNotificationSetting(
                      "emailPollMinutes",
                      e.target.value
                    )
                  }
                  className="max-w-32"
                />
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  minutes
                </span>
              </div>
            </Field>

            <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              <FiLock className="mt-0.5 shrink-0 text-blue-600" />
              <p>
                The email password and WhatsApp access details remain secure on
                the server. They are not shown on this page.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </Can>
  );

  const renderRolesTab = () => (
    <Can
      moduleKey="roles"
      action="read"
      fallback={<EmptyAccessCard title="roles and permissions" />}
    >
      <RoleManagement />
    </Can>
  );

  const renderUsersTab = () => (
    <Can
      moduleKey="users"
      action="read"
      fallback={<EmptyAccessCard title="user access" />}
    >
      <UserManagement />
    </Can>
  );

  const renderAppearanceTab = () => (
    <Card>
      <SectionHeader
        icon={FiSun}
        title="Appearance"
        subtitle="Choose how your dashboard looks."
      />

      <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-2">
        <button
          type="button"
          onClick={() => setTheme("light")}
          className={`rounded-2xl border p-5 text-left transition ${
            theme === "light"
              ? "border-blue-500 bg-blue-50 ring-4 ring-blue-50 dark:bg-blue-950 dark:ring-blue-950"
              : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-orange-500 dark:bg-orange-950">
              <FiSun className="text-xl" />
            </div>

            {theme === "light" ? (
              <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                Active
              </span>
            ) : null}
          </div>

          <h3 className="mt-5 font-bold text-slate-950 dark:text-slate-50">
            Light Mode
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Bright, clean, and easy to read.
          </p>
        </button>

        <button
          type="button"
          onClick={() => setTheme("dark")}
          className={`rounded-2xl border p-5 text-left transition ${
            theme === "dark"
              ? "border-blue-500 bg-blue-50 ring-4 ring-blue-50 dark:bg-blue-950 dark:ring-blue-950"
              : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <FiMoon className="text-xl" />
            </div>

            {theme === "dark" ? (
              <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                Active
              </span>
            ) : null}
          </div>

          <h3 className="mt-5 font-bold text-slate-950 dark:text-slate-50">
            Dark Mode
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Softer interface for low-light work.
          </p>
        </button>
      </div>
    </Card>
  );

  const renderActiveTab = () => {
    if (activeTab === "profile") return renderProfileTab();
    if (activeTab === "password") return renderPasswordTab();
    if (activeTab === "company") return renderCompanyTab();
    if (activeTab === "invoice") return renderInvoiceTab();
    if (activeTab === "notifications") return renderNotificationsTab();
    if (activeTab === "roles") return renderRolesTab();
    if (activeTab === "users") return renderUsersTab();
    if (activeTab === "appearance") return renderAppearanceTab();

    return renderProfileTab();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 dark:bg-slate-950">
        <Card className="p-6">
          <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
            <FiRefreshCw className="animate-spin" />
            Loading settings...
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100 sm:px-6">
      <FeedbackModal
        open={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={closeFeedback}
      />

      <div className="mx-auto max-w-[1500px]">
        <div className="mb-6">
          <h1 className="text-3xl font-black tracking-tight text-slate-950 dark:text-slate-50">
            Settings
          </h1>

          <p className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Manage profile, security, company information, invoices, document
            automation, WhatsApp alerts, role access, users, and appearance.
          </p>
        </div>

        <div className="mb-6 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
          <div className="flex min-w-max gap-8">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative whitespace-nowrap px-1 pb-4 text-sm font-semibold transition ${
                    isActive
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-100"
                  }`}
                >
                  {tab.label}

                  {isActive ? (
                    <span className="absolute bottom-[-1px] left-0 h-0.5 w-full rounded-full bg-blue-600" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-6">
          {renderActiveTab()}

          {showSaveBar ? (
            <Card className="sticky bottom-4 z-20 flex flex-col gap-3 border-slate-200/90 bg-white/95 p-4 backdrop-blur dark:bg-slate-900/95 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || savingEmailAutomation}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiSave />
                {saving || savingEmailAutomation
                  ? "Saving..."
                  : "Save Changes"}
              </button>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}